import { CompanyStatuses, SecurityRiskLevels, messages } from '@nesc/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

/** 絞り込みの全条件（スマホではボトムシート） */
export const CompanyFilterSheet = ({
  open,
  onOpenChange,
  filters,
  onChange,
  onReset,
  assignees,
  tags,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: Record<string, string | string[] | undefined>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
  assignees: { id: string; name: string | null; email: string }[];
  tags: { id: string; name: string }[];
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{messages.common.filter}</DialogTitle>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <Field label="営業状況">
          {(props) => (
            <Select
              {...props}
              value={(filters.status as string) ?? ''}
              onChange={(e) => onChange('status', e.target.value)}
            >
              <option value="">{messages.common.all}</option>
              {CompanyStatuses.list.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="担当者">
          {(props) => (
            <Select
              {...props}
              value={(filters.assigneeId as string) ?? ''}
              onChange={(e) => onChange('assigneeId', e.target.value)}
            >
              <option value="">{messages.common.all}</option>
              <option value="unassigned">{messages.common.unassigned}</option>
              {assignees.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name ?? user.email}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="タグ">
          {(props) => (
            <Select
              {...props}
              value={(filters.tagIds as string) ?? ''}
              onChange={(e) => onChange('tagIds', e.target.value)}
            >
              <option value="">{messages.common.all}</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="都道府県">
            {(props) => (
              <Input
                {...props}
                value={(filters.prefecture as string) ?? ''}
                onChange={(e) => onChange('prefecture', e.target.value)}
                placeholder="東京都"
              />
            )}
          </Field>
          <Field label="業種">
            {(props) => (
              <Input
                {...props}
                value={(filters.industry as string) ?? ''}
                onChange={(e) => onChange('industry', e.target.value)}
                placeholder="食品卸"
              />
            )}
          </Field>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-body font-semibold">連絡先</legend>
          <p className="text-note text-foreground-muted">
            送信できる手段で絞り込みます。無効にした連絡先は「なし」として扱います。
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { key: 'hasEmail', label: 'メールアドレス' },
              { key: 'hasForm', label: '問い合わせフォーム' },
              { key: 'hasPhone', label: '電話番号' },
              { key: 'hasAnyContact', label: 'いずれかの連絡手段' },
            ].map((item) => (
              <Field key={item.key} label={item.label}>
                {(props) => (
                  <Select
                    {...props}
                    value={(filters[item.key] as string) ?? ''}
                    onChange={(e) => onChange(item.key, e.target.value)}
                  >
                    <option value="">指定しない</option>
                    <option value="true">ある</option>
                    <option value="false">ない</option>
                  </Select>
                )}
              </Field>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-body font-semibold">セキュリティ</legend>
          <p className="text-note text-foreground-muted">
            直近のセキュリティチェックの結果で絞り込みます。
          </p>
          <Field label="実施状況">
            {(props) => (
              <Select
                {...props}
                value={(filters.securityChecked as string) ?? ''}
                onChange={(e) => onChange('securityChecked', e.target.value)}
              >
                <option value="">{messages.common.all}</option>
                <option value="true">実施済み</option>
                <option value="false">未実施</option>
              </Select>
            )}
          </Field>
          <div className="space-y-2">
            <p className="text-note text-foreground-muted">
              判定（複数選ぶとどれかに当てはまる企業）
            </p>
            {SecurityRiskLevels.list.map((level) => {
              const selected = ((filters.securityRisk as string) ?? '').split(',').filter(Boolean);
              return (
                <label key={level.value} className="flex min-h-touch items-center gap-2 text-body">
                  <input
                    type="checkbox"
                    className="size-4 accent-foreground"
                    checked={selected.includes(level.value)}
                    onChange={(e) =>
                      onChange(
                        'securityRisk',
                        (e.target.checked
                          ? [...selected, level.value]
                          : selected.filter((v) => v !== level.value)
                        ).join(','),
                      )
                    }
                  />
                  <span>{level.label}</span>
                  <span className="text-note text-foreground-subtle">{level.description}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-body font-semibold">その他</legend>
          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={filters.excluded === 'true'}
              onChange={(e) => onChange('excluded', e.target.checked ? 'true' : '')}
            />
            対象外のみ表示する（既定では出しません）
          </label>
          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={filters.lowConfidenceOnly === 'true'}
              onChange={(e) => onChange('lowConfidenceOnly', e.target.checked ? 'true' : '')}
            />
            確度が低い項目を含む企業のみ
          </label>
        </fieldset>

        <Field label="最終接触日（これより前）">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={(filters.lastContactedBefore as string) ?? ''}
              onChange={(e) => onChange('lastContactedBefore', e.target.value)}
            />
          )}
        </Field>
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" size="lg" onClick={onReset}>
          {messages.common.reset}
        </Button>
        <Button size="lg" onClick={() => onOpenChange(false)}>
          {messages.common.apply}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
